import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';

export const FallingLeaves = ({ color = '#ffc0cb', count = 20 }) => {
  const leavesRef = useRef([]);
  const isInitialSpawn = useRef(true);

  useEffect(() => {
    const FALL_DURATION = 20; // Consistent fall duration
    
    leavesRef.current.forEach((leaf, index) => {
      const startX = Math.random() * window.innerWidth;
      const startY = isInitialSpawn.current 
        ? Math.random() * window.innerHeight // Random Y for initial spawn
        : -50; // Above screen for subsequent spawns
      
      const animate = (x, y) => {
        gsap.fromTo(leaf,
          {
            x: x,
            y: y,
            rotation: 0
          },
          {
            y: window.innerHeight,
            x: x + (Math.random() * 200 - 100), // Horizontal drift
            rotation: 360,
            duration: (window.innerHeight - y) / window.innerHeight * FALL_DURATION,
            ease: 'none',
            onComplete: () => {
              // Reset to random position above screen
              gsap.set(leaf, {
                rotation: 0
              });
              animate(Math.random() * window.innerWidth, -50);
            }
          }
        );
      };
      
      // Stagger initial animations slightly
      setTimeout(() => {
        animate(startX, startY);
      }, isInitialSpawn.current ? index * 100 : 0);
    });

    isInitialSpawn.current = false;

    return () => {
      leavesRef.current.forEach(leaf => {
        gsap.killTweensOf(leaf);
      });
    };
  }, []);

  return (
    <div className="absolute inset-0 z-5 pointer-events-none">
      {[...Array(count)].map((_, i) => (
        <div
          key={i}
          ref={el => leavesRef.current[i] = el}
          style={{
            position: 'absolute',
            width: '8px',
            height: '8px',
            borderRadius: '150% 0 150% 0',
            background: color,
            boxShadow: `0 0 10px ${color}80, 0 0 20px ${color}40, 0 0 30px ${color}20`,
          }}
        />
      ))}
    </div>
  );
};

export const Flash = ({ color = '#ffffff', duration = 0.5 }) => {
  const flashRef = useRef();

  useEffect(() => {
    gsap.fromTo(flashRef.current,
      { opacity: 1 },
      { opacity: 0, duration, ease: "power2.out" }
    );
  }, [duration]);

  return (
    <div
      ref={flashRef}
      className="absolute inset-0 z-40 pointer-events-none"
      style={{ backgroundColor: color }}
    />
  );
};

export const Vignette = ({ intensity = 0.5 }) => {
  return (
    <div 
      className="absolute inset-0 z-30 pointer-events-none"
      style={{
        background: `radial-gradient(circle, transparent 0%, rgba(0,0,0,${intensity}) 100%)`
      }}
    />
  );
};

export const Overlay = ({ overlayType, gameLogic, opacity = 0.5 }) => {
  /* available types are: fireOverlay, bloodOverlay, breakingGlassOverlay */
  return (
    <div className="absolute inset-0 z-40 pointer-events-none">
      <img 
        src={gameLogic.getSpriteUrl(overlayType)}
        alt={overlayType}
        className="w-full h-full object-cover"
        style={{ mixBlendMode: 'screen', opacity: opacity }}
      />
    </div>
  );
};

export const MotionLines = ({ duration = 1, rotation = -15 }) => {
  const linesRef = useRef();

  useEffect(() => {
    const lines = linesRef.current.children;
    const angleInRadians = (rotation * Math.PI) / 180;
    const distance = window.innerWidth * 1.5;
    
    Array.from(lines).forEach((line, i) => {
      const randomDuration = duration * (0.3 + Math.random() * 0.4);
      const randomStart = -300 + Math.random() * 200;
      
      const startX = -distance * Math.cos(angleInRadians);
      const startY = -distance * Math.sin(angleInRadians);
      const endX = distance * Math.cos(angleInRadians);
      const endY = distance * Math.sin(angleInRadians);

      gsap.fromTo(
        line,
        { 
          x: startX,
          y: startY,
          opacity: 0 
        },
        {
          x: endX,
          y: endY,
          opacity: 1,
          duration: randomDuration,
          repeat: -1,
          ease: 'none',
          delay: i * 0.05,
        }
      );
    });

    return () => {
      Array.from(lines).forEach((line) => {
        gsap.killTweensOf(line);
      });
    };
  }, [duration, rotation]);

  return (
    <div
      ref={linesRef}
      className="absolute inset-0 z-20 overflow-hidden pointer-events-none"
      style={{ filter: 'blur(1px)' }}
    >
      {[...Array(25)].map((_, i) => (
        <div
          key={i}
          className="absolute h-[4px] bg-white/40"
          style={{
            width: '400px',
            top: `${i * 40 + Math.random() * 20}px`,
            left: '50%',
            transform: `rotate(${rotation}deg)`,
            transformOrigin: 'center',
          }}
        />
      ))}
    </div>
  );
};
